import React, { useState, useEffect } from "react";
import { ArrowLeft, Plus, Users } from "lucide-react";
import "../CSS/register.css";
import "../CSS/logoutModal.css";
import logo from "../assets/logo.png";

function Register() {
  const [formData, setFormData] = useState({
    cid: "",
    name: "",
    age: "",
    gender: "",
    email: "",
    complaint: "",
    tokenNumber: "",
    chamberAssignment: "",
  });

  const [patients, setPatients] = useState([]);
  const [errors, setErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
    setErrors({ ...errors, [e.target.id]: "" });
  };

  const validateForm = () => {
    let newErrors = {};

    if (!formData.cid.trim()) newErrors.cid = "CID number is required";
    if (formData.cid && formData.cid.length !== 11)
      newErrors.cid = "CID must be exactly 11 digits";

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.age) newErrors.age = "Age is required";
    if (!formData.gender) newErrors.gender = "Gender is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    if (!formData.complaint.trim())
      newErrors.complaint = "Chief complaint is required";
    if (!formData.tokenNumber)
      newErrors.tokenNumber = "Token number is required";
    if (!formData.chamberAssignment)
      newErrors.chamberAssignment = "Please select a chamber";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const response = await fetch("https://q-less.onrender.com/api/patient/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setShowSuccessModal(true);
        fetchPatients();

        setFormData({
          cid: "",
          name: "",
          age: "",
          gender: "",
          email: "",
          complaint: "",
          tokenNumber: "",
          chamberAssignment: "",
        });
        setErrors({});
      } else {
        setErrors({ general: data.message });
      }
    } catch (error) {
      setErrors({ general: "Server error. Please try again." });
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await fetch("https://q-less.onrender.com/api/patient/list");
      const data = await response.json();
      setPatients(data);
    } catch (error) {
      console.error("Error fetching patients:", error);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  const confirmLogout = () => {
    window.location.href = "/stafflogin";
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  return (
    <div className="registerpage-container">
      <header className="registerpage-header">
        <div className="registerpage-header-left">
          <ArrowLeft size={20} className="registerpage-backicon" />
          <img src={logo} alt="Q-Less Logo" className="registerpage-logo" />
        </div>

        <div className="registerpage-header-right" style={{ display: "flex", gap: "15px", alignItems: "center" }}>
          <button
            onClick={() => setShowLogoutModal(true)}
            style={{
              background: "#ef4444",
              color: "white",
              padding: "6px 14px",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            Logout
          </button>
        </div>
      </header>

      {/* LOGOUT MODAL */}
      {showLogoutModal && (
        <div className="logout-overlay">
          <div className="logout-modal">
            <h3>Are you sure you want to log out?</h3>
            <div className="logout-buttons">
              <button className="cancel-btn" onClick={cancelLogout}>Cancel</button>
              <button className="yes-btn" onClick={confirmLogout}>Yes</button>
            </div>
          </div>
        </div>
      )}

      {/* SUCCESS MODAL */}
      {showSuccessModal && (
        <div className="modal-overlay">
          <div className="success-modal">
            <div className="modal-icon">✓</div>
            <h2>Patient Registration Successful</h2>
            <button className="modal-btn" onClick={() => setShowSuccessModal(false)}>Continue</button>
          </div>
        </div>
      )}

      <main className="registerpage-main">
        <div className="registerpage-card">
          <div className="registerpage-card-header">
            <Plus size={38} className="registerpage-plusicon" />
            <h2>Register Patient</h2>
            <p>Enter patient details to assign token and chamber</p>
          </div>

          {/* FORM */}
          <form className="registerpage-form" onSubmit={handleSubmit}>
            {/* ROW 1: CID + NAME */}
            <div className="registerpage-form-row">
              <div className="registerpage-form-group">
                <label>CID Number</label>
                <input
                  id="cid"
                  type="text"
                  value={formData.cid}
                  onChange={handleChange}
                  placeholder="Enter CID Number"
                />
                {errors.cid && <p className="error-msg">{errors.cid}</p>}
              </div>

              <div className="registerpage-form-group">
                <label>Name</label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter Name"
                />
                {errors.name && <p className="error-msg">{errors.name}</p>}
              </div>
            </div>

            {/* ROW 2: AGE + GENDER */}
            <div className="registerpage-form-row">
              <div className="registerpage-form-group">
                <label>Age</label>
                <input
                  id="age"
                  type="number"
                  value={formData.age}
                  onChange={handleChange}
                  placeholder="Enter Age"
                />
                {errors.age && <p className="error-msg">{errors.age}</p>}
              </div>

              <div className="registerpage-form-group">
                <label>Gender</label>
                <select
                  id="gender"
                  value={formData.gender}
                  onChange={handleChange}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                {errors.gender && <p className="error-msg">{errors.gender}</p>}
              </div>
            </div>

            {/* ROW 3: EMAIL + COMPLAINT */}
            <div className="registerpage-form-row">
              <div className="registerpage-form-group">
                <label>Email</label>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter Email"
                />
                {errors.email && <p className="error-msg">{errors.email}</p>}
              </div>

              <div className="registerpage-form-group">
                <label>Chief Complaint</label>
                <input
                  id="complaint"
                  type="text"
                  value={formData.complaint}
                  onChange={handleChange}
                  placeholder="Reason, Sickness, Disease, Check Ups"
                />
                {errors.complaint && <p className="error-msg">{errors.complaint}</p>}
              </div>
            </div>

            {/* ROW 4: TOKEN + CHAMBER */}
            <div className="registerpage-form-row">
              <div className="registerpage-form-group">
                <label>Token Number</label>
                <input
                  id="tokenNumber"
                  type="number"
                  value={formData.tokenNumber}
                  onChange={handleChange}
                  placeholder="Enter Token Number"
                />
                {errors.tokenNumber && <p className="error-msg">{errors.tokenNumber}</p>}
              </div>

              <div className="registerpage-form-group">
                <label>Chamber Assignment</label>
                <select
                  id="chamberAssignment"
                  value={formData.chamberAssignment}
                  onChange={handleChange}
                >
                  <option value="">Select Chamber</option>
                  <option>Chamber #1</option>
                  <option>Chamber #2</option>
                  <option>Chamber #3</option>
                </select>
                {errors.chamberAssignment && <p className="error-msg">{errors.chamberAssignment}</p>}
              </div>
            </div>

            {errors.general && <p className="error-msg" style={{ textAlign: "center" }}>{errors.general}</p>}

            <button type="submit" className="registerpage-btn">Register Patient</button>
          </form>
        </div>

        {/* RECENT PATIENTS */}
        <section className="registerpage-recent">
          <div className="registerpage-recent-header">
            <Users size={18} />
            <h3>Recent Registrations</h3>
          </div>

          <table className="registerpage-table">
            <thead>
              <tr>
                <th>CID</th>
                <th>Name</th>
                <th>Age</th>
                <th>Gender</th>
                <th>Token</th>
                <th>Chamber</th>
              </tr>
            </thead>

            <tbody>
              {patients.map((p) => (
                <tr key={p._id}>
                  <td>{p.cid}</td>
                  <td>{p.name}</td>
                  <td>{p.age}</td>
                  <td>{p.gender}</td>
                  <td>{p.tokenNumber}</td>
                  <td>{p.chamberAssignment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </main>

      <footer className="registerpage-footer">
        © 2025 Q-Less. All Rights Reserved.
      </footer>
    </div>
  );
}

export default Register;
